import React, { useState, useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { InitialStateUIType, InitialStateUserType } from "../../typings/state";
import "../riasec/riasec-test.scss";
import { useBreadcrumbSetter } from "../../hooks/useBreadcrumbSetter";
import { pomService } from "../../services/pom.service";
import {
  PomQuestion,
  PomAnswer,
  PomLastTestResponse,
} from "../../typings/pom";
import PomTestIntro from "../../components/Pom/PomTestIntro";
import PomTestQuestion from "../../components/Pom/PomTestQuestion";
import PomTestResult from "../../components/Pom/PomTestResult";

interface PomTestProps extends RouteComponentProps {
  user: InitialStateUserType;
  UI: InitialStateUIType;
}

const PomTest: React.FC<PomTestProps> = () => {
  const [lastTest, setLastTest] = useState<PomLastTestResponse | null>(null);
  const [questions, setQuestions] = useState<PomQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [testResult, setTestResult] = useState<PomLastTestResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [showAnswersSummary, setShowAnswersSummary] = useState(false);

  const breadcrumbs = useMemo(
    () => [{ label: "MBTI Assessment", isLast: true }],
    []
  );

  useBreadcrumbSetter(breadcrumbs);

  useEffect(() => {
    loadLastTest();
  }, []);

  const loadLastTest = async () => {
    try {
      const response = await pomService.getLastTest();
      if(response?.status){
        setLastTest(response as PomLastTestResponse);
      }
    } catch (error) {
      console.error("Error loading last test:", error);
    }
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const response = await pomService.getAllQuestions(true);
      setQuestions(response.data);
      setIsTestStarted(true);
      setCurrentQuestionIndex(0);
      setAnswers({});
    } catch (error) {
      console.error("Error loading questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: number, answer: number) => {
    // Prevent multiple clicks during transition
    if (loading) return;

    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));

    // Set loading state and auto-advance to next question after a short delay
    setLoading(true);
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      }
      setLoading(false);
    }, 500);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const answersArray: PomAnswer[] = Object.entries(answers).map(
        ([questionId, answer]) => ({
          question_id: Number.parseInt(questionId, 10),
          answer,
        })
      );

      const response = await pomService.submitTest({
        answers: answersArray,
      });
      setTestResult(response);
      setIsTestCompleted(true);
    } catch (error) {
      console.error("Error submitting test:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    setIsTestStarted(false);
    setIsTestCompleted(false);
    setTestResult(null);
    loadLastTest();
  };

  const handleViewLastTestDetail = () => {
    setTestResult(lastTest);
    setIsTestCompleted(true);
  };

  const handleToggleSummary = (show: boolean) => {
    setShowAnswersSummary(show);
  };

  if (isTestCompleted && testResult) {
    return (
      <PomTestResult
        testResult={testResult}
        onBackToHome={handleBackToHome}
      />
    );
  }

  if (isTestStarted && questions.length > 0) {
    return (
      <PomTestQuestion
        questions={questions}
        currentQuestionIndex={currentQuestionIndex}
        answers={answers}
        loading={loading}
        showAnswersSummary={showAnswersSummary}
        onAnswer={handleAnswer}
        onNext={handleNext}
        onPrev={handlePrev}
        onSubmit={handleSubmit}
        onToggleSummary={handleToggleSummary}
      />
    );
  }

  return (
    <PomTestIntro
      loading={loading}
      lastTest={lastTest}
      onStartTest={startTest}
      onViewLastTestDetail={handleViewLastTestDetail}
    />
  );
};

export default PomTest;
